import { message } from 'antd';
import React, { useImperativeHandle, useRef } from 'react';

import { DraggerRule } from '@/components/common/Dragger';
import DraggerUpload, {
  DraggerUploadProps,
} from '@/components/UIComponents/DraggerUpload';
import { UseTranslateType } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';
import { getMediaDuration } from '@/utils/fileUtil';
import { checkFileDuration, checkFileSize } from '@/utils/fileValidate';

import DeleteIcon from '../../icon/delete';
import RingIcon from '../../icon/ring';
import { VideoPreviewContent } from '../../preview';
import UploadAudioSuccess from './uploadAudioSuccess';

export type UrlData = {
  url: string;
  name: string;
  type: 'video' | 'audio';
  detailType: string;
  duration: number;
  size: number;
  format: string;
};

interface DraggerContentProps {
  t: UseTranslateType;
  onChange: (data: UrlData) => void;
  onClear: () => void;
}
export interface DraggerContentRef {
  getData: () => UrlData;
}

const isVideo = (type: string) =>
  ['.mp4', 'video/mp4', '.mov', 'video/quicktime'].includes(type);
const isAudio = (type: string) =>
  ['.mp3', 'audio/mpeg', '.wav', 'audio/wav'].includes(type);

const DraggerContent = React.forwardRef<DraggerContentRef, DraggerContentProps>(
  (props, ref) => {
    const { t } = props;
    const urlData = useRef<UrlData>({
      url: '',
      name: '',
      type: 'video',
      detailType: '',
      duration: 0,
      size: 0,
      format: '',
    });
    const requirements = [
      {
        label: t('boom.0.9.voicesModal.tip.video'),
        value: 'MP4/MOV',
      },
      {
        label: t('boom.0.9.voicesModal.tip.audio'),
        value: 'MP3/WAV/M4A',
      },
    ];
    const rules: DraggerRule[] = [
      {
        type: 'accept',
        accept: [
          '.mp4',
          'video/mp4',
          '.mov',
          'video/quicktime',
          '.mp3',
          'audio/mpeg',
          '.wav',
          'audio/wav',
          '.m4a',
          'audio/mp4',
          'audio/x-m4a',
        ],
        message: t('bc.file.valid.typeError'),
      },
      {
        type: 'validator',
        validator: async (files: FileList) => {
          const { isValid: durationValid } = await checkFileDuration(files, {
            min: 10,
            max: 3 * 60,
          });
          if (!durationValid) {
            message.warning(`${t('boom.0.9.filedura')}`);
            return false;
          }
          urlData.current = {
            url: '',
            duration: await getMediaDuration(files[0]),
            size: files[0].size,
            type: isVideo(files[0].type) ? 'video' : 'audio',
            detailType: files[0].type,
            name: files[0].name,
            format: files[0].type,
          };

          if (isVideo(files[0].type)) {
            if (!checkFileSize(files, { max: 500 * 1024 * 1024 })) {
              message.warning(`${t('boom.0.9.videomb', { bits: 500 })}`);
              return false;
            }
          }
          if (isAudio(files[0].type)) {
            if (!checkFileSize(files, { max: 100 * 1024 * 1024 })) {
              message.warning(`${t('boom.0.9.audiomb', { bits: 100 })}`);
              return false;
            }
          }
          return true;
        },
      },
    ];

    useImperativeHandle(ref, () => ({
      getData: () => {
        return urlData.current;
      },
    }));

    const config: DraggerUploadProps = {
      rules,
      fileType: 'audio',
      eventTracking: {
        from: AIFunction.VoiceClone,
      },
      ready: {
        slotNode: (ref) => (
          <div
            ref={ref}
            className="relative flex flex-col justify-center items-center h-[190px] gap-[8px]  rounded-[8px]"
          >
            <div
              style={{
                borderRadius: '8px 8px 0 0',
                background:
                  'linear-gradient(0deg, rgba(4, 14, 121, 0.00) -100%, rgba(62, 62, 236, 0.20) 100%)',
              }}
              className=" text-[12px] text-[#636EF4] tracking-[0.32px] w-full h-[22px] flex justify-center items-center absolute top-0 left-0"
            >
              <RingIcon className="text-[#636EF4]" />
              &nbsp; {t('boom.0.9.audiobetter')}
            </div>

            <div className="bg-[#4931FA] w-[30px] h-[30px] rounded-full flex justify-center items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M6.75 4.5L9 2.25M9 2.25L11.25 4.5M9 2.25V9.75M5.25017 7.5C4.55126 7.5 4.2018 7.5 3.92615 7.61418C3.55861 7.76642 3.26642 8.05861 3.11418 8.42615C3 8.7018 3 9.05109 3 9.75V13.35C3 14.1901 3 14.6098 3.16349 14.9307C3.3073 15.2129 3.5366 15.4429 3.81885 15.5867C4.1394 15.75 4.55925 15.75 5.39768 15.75H12.6027C13.4411 15.75 13.8604 15.75 14.1809 15.5867C14.4632 15.4429 14.6929 15.2129 14.8367 14.9307C15 14.6101 15 14.1908 15 13.3523V9.75C15 9.05109 14.9999 8.7018 14.8857 8.42615C14.7335 8.05861 14.4416 7.76642 14.074 7.61418C13.7984 7.5 13.4489 7.5 12.75 7.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-app-tertiary text-[10px] w-[288px] text-center">
              {t('MP4-MOV-10-3-500M-100M')}
            </p>
          </div>
        ),
      },
      success: {
        onSuccess: (url: string) => {
          urlData.current.url = url;
          props.onChange(urlData.current);
        },
        slotNode(url, file, onClear) {
          return (
            <div className="w-full  relative h-[190px]">
              <div
                className="absolute right-[10px] top-[10px]  cursor-pointer z-[100]"
                onClick={(e) => {
                  e && e.stopPropagation();
                  urlData.current = {
                    url: '',
                    name: '',
                    type: 'video',
                    detailType: '',
                    duration: 0,
                    size: 0,
                    format: '',
                  };
                  onClear();
                  props.onClear();
                }}
              >
                <DeleteIcon />
              </div>

              {urlData.current.type === 'video' ? (
                <div className="h-[190px]">
                  <VideoPreviewContent url={url} />
                </div>
              ) : (
                <UploadAudioSuccess url={url} name={file.name} />
              )}
            </div>
          );
        },
      },
    };
    return <DraggerUpload {...config} />;
  },
);

export default DraggerContent;
